// NATIVE
import { useEffect, useState } from 'react';

// INSTALLED
import { Button, Divider } from '@mui/material';

// CUSTOM
import { mtDividerUpTable } from '../../commons';
import { hasPemissions } from '../../commons/utils';
import { TableCargos } from '../../components/tables/TableCargos';
import { CreateDialogCargo } from '../../components/modals/cargos/CreateDialogCargo';
import { BreadCrumbsAndTitle } from '../../components/generics/BreadCrumbsAndTitle';
import { AlertsInformativos } from '../../components/generics/AlertsInformativos';
import { alertInformativo } from '../../commons/texts/textAlertsInformativos';

/**
 * Cargos component renders the cargos page with functionalities to create new cargos.
 *
 * @component
 *
 * @example
 * return (
 *   <Cargos />
 * )
 *
 * @returns {JSX.Element} The rendered cargos page component.
 *
 * @remarks
 * This component uses the following hooks:
 * - `useState` to manage the state of the cargo creation modal and the permission to create cargos.
 * - `useEffect` to check permissions when the component mounts.
 *
 * @function
 * @name Cargos
 *
 * @description
 * The Cargos component includes:
 * - A breadcrumb and title section.
 * - A button to open the cargo creation modal if the user has the necessary permissions.
 * - A divider for visual separation.
 * - A modal dialog for creating cargos.
 * - A table displaying the list of cargos.
 *
 * @internal
 * @async
 * @function
 * @name hasPrmissionsCreate
 * @description Checks if the user has permissions to create cargos and updates the state accordingly.
 *
 * @internal
 * @function
 * @name openModal
 * @description Opens the cargo creation modal by updating the state.
 *
 * @internal
 * @function
 * @name handleCloseDialogCrearCargo
 * @description Closes the cargo creation modal by updating the state.
 */
export const Cargos = () => {
  // USE STATE
  const [openCargo, setOpenCargo] = useState(false);
  const [canCreate, setCanCreate] = useState(false);

  // USE EFFECT
  useEffect(() => {
    hasPrmissionsCreate();
  }, []);

  // CONST FUNCTIONS
  const hasPrmissionsCreate = async () => {
    const hasPermissions = await hasPemissions('modulo.configuracion', 'modulo.cargos', 'create');

    if (hasPermissions) {
      setCanCreate(true);
    }
  };

  // FUNCTIONS OPEN MODALS
  const openModal = () => {
    setOpenCargo(true);
  };

  const handleCloseDialogCrearCargo = () => {
    setOpenCargo(false);
  };

  return (
    <div>
      <BreadCrumbsAndTitle
        title="Listado Cargos"
        mainModule="configuracion"
        parent="Listado Cargos"
        action=""
      />
      {canCreate && (
        <div style={{ display: 'flex', justifyContent: 'end' }}>
          <Button variant="contained" sx={{ marginBottom: 3 }} onClick={openModal}>
            Crear Cargo
          </Button>
        </div>
      )}

      <AlertsInformativos type={'info'} title={'info'} content={alertInformativo.cargo} />
      <Divider sx={{ marginBottom: mtDividerUpTable }} />

      <CreateDialogCargo
        open={openCargo}
        handleCloseDialogCrearCargo={handleCloseDialogCrearCargo}
      />
      <TableCargos />
    </div>
  );
};
